import React, { useState, useEffect, useRef, useMemo } from 'react';
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import { FiCopy, FiChevronDown, FiChevronUp, FiCheck, FiUserCheck, FiUserX, FiPlus } from 'react-icons/fi';
import axios from 'axios';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Verificacion from '../../../components/Verificacion';
import BASE_URL from '../../../config/config';
import { cabecerasAuth, claves } from '../../../queries/cliente';
import Loader from '../../../components/Loader';
import FiltroBusqueda from '../../../components/ui/FiltroBusqueda';
import { useTranslation } from 'react-i18next';

const ESTADOS_TICKET = [
  'abierto',
  'retenido',
  'cerrado',
];

const DEPARTAMENTOS = [
  'no_especificado',
  'diseno',
  'marketing',
  'desarrollo',
  'redes_sociales',
];

const ESTADOS_CHAT = [
  'sin_responder',
  'respondido',
];

const PRIORIDADES = [
  'no_urgente',
  'urgente',
];

const UMBRAL_SIN_ACTIVIDAD_HORAS = 120; // 5 días
const UMBRAL_ALERTA_HORAS = 4;

// Cuenta mensajes del cliente acumulados al final del chat que el staff aún no ha respondido.
const contarMensajesPendientes = (chatRaw) => {
  let chat = chatRaw;
  if (typeof chat === 'string') {
    try {
      chat = JSON.parse(chat);
    } catch (err) {
      return 0;
    }
  }
  if (!Array.isArray(chat)) return 0;
  let count = 0;
  for (let i = chat.length - 1; i >= 0; i -= 1) {
    if (chat[i].role !== 'user') break;
    count += 1;
  }
  return count;
};

const formatearFechaRelativa = (fecha, t, idioma) => {
  const diffMs = Date.now() - new Date(fecha).getTime();
  const minutos = Math.floor(diffMs / 60000);
  if (minutos < 1) return t('gestorTickets.ahora');
  if (minutos < 60) return t('gestorTickets.haceMinutos', { count: minutos });
  const horas = Math.floor(minutos / 60);
  if (horas < 24) return t('gestorTickets.haceHoras', { count: horas });
  const dias = Math.floor(horas / 24);
  if (dias < 30) return t('gestorTickets.haceDias', { count: dias });
  return new Date(fecha).toLocaleDateString(idioma);
};

const toggleArrayValue = (arr, value, setter) => {
  setter(arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value]);
};

// dpto usa guiones bajos en los datos pero las clases CSS de color usan guiones
const claseDpto = (v) => (v === 'redes_sociales' ? 'redes-sociales' : v === 'no_especificado' ? 'no-especificado' : v);

const obtenerValorOrden = (ticket, campo) => {
  if (campo === 'createdAt') return new Date(ticket.createdAt).getTime();
  return (ticket[campo] || '').toString().toLowerCase();
};

/** Filtros dejados por otra pantalla (KPIs del panel, ficha de usuario…). */
const leerFiltrosGuardados = () => {
  try {
    const guardados = localStorage.getItem('ticketManagerFiltros');
    return guardados ? JSON.parse(guardados) : {};
  } catch (err) {
    console.error('Error al cargar filtros guardados:', err);
    return {};
  }
};

const TicketManager = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const estadosTicket = ESTADOS_TICKET.map(value => ({ value, label: t(`gestorTickets.estados.${value}`) }));
  const departamentos = DEPARTAMENTOS.map(value => ({ value, label: t(`gestorTickets.departamentos.${value}`) }));
  const estadosChat = ESTADOS_CHAT.map(value => ({ value, label: t(`gestorTickets.estadosChat.${value}`) }));
  const prioridades = PRIORIDADES.map(value => ({ value, label: t(`gestorTickets.prioridades.${value}`) }));

  const queryClient = useQueryClient();
  const [minimoTiempoCumplido, setMinimoTiempoCumplido] = useState(false);

  // Se lee una sola vez, al montar, y sirve de valor inicial de cada filtro
  const guardados = useRef(leerFiltrosGuardados()).current;

  const [currentPage, setCurrentPage] = useState(1);
  const [ticketsPerPage, setTicketsPerPage] = useState(20);
  const [searchText, setSearchText] = useState(guardados.searchText || '');
  // Filtro por creador: lo fija la ficha de usuario al pulsar "ver sus tickets"
  const [creador, setCreador] = useState(guardados.creador || '');

  // Filtros del menú lateral (multi-selección)
  const [selectedEstados, setSelectedEstados] = useState(guardados.selectedEstados || []);
  const [selectedDepts, setSelectedDepts] = useState(guardados.selectedDepts || []);
  const [selectedChats, setSelectedChats] = useState(guardados.selectedChats || []);
  const [selectedPrioridades, setSelectedPrioridades] = useState(guardados.selectedPrioridades || []);
  const [selectedTecnicos, setSelectedTecnicos] = useState(guardados.selectedTecnicos || []);
  const [fechaDesde, setFechaDesde] = useState(guardados.fechaDesde || '');
  const [fechaHasta, setFechaHasta] = useState(guardados.fechaHasta || '');

  // Filtros rápidos (chips)
  const [soloMios, setSoloMios] = useState(!!guardados.soloMios);
  const [soloSinActividad, setSoloSinActividad] = useState(!!guardados.soloSinActividad);
  const [soloAlerta, setSoloAlerta] = useState(!!guardados.soloAlerta);

  // Orden
  const [sortField, setSortField] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');

  // Selección múltiple / acciones masivas
  const [seleccionados, setSeleccionados] = useState(new Set());
  const [procesandoBulk, setProcesandoBulk] = useState(false);

  // Desplegables personalizados (estado/dpto/prioridad/técnico inline y acciones masivas)
  const [menuAbierto, setMenuAbierto] = useState(null); // { scope, id, campo } | null

  // Vistas guardadas
  const [vistasGuardadas, setVistasGuardadas] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('ticketManagerVistas') || '[]');
    } catch (err) {
      return [];
    }
  });
  const [nombreNuevaVista, setNombreNuevaVista] = useState('');

  const [mostrarFiltros, setMostrarFiltros] = useState(false);

  const token = localStorage.getItem("AuthToken");
  const tokenInfo = token ? jwtDecode(token) : {};

  // Ref para el menú lateral
  const menuRef = useRef(null);

  // Cerrar menú al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mostrarFiltros && menuRef.current && !menuRef.current.contains(event.target)) {
        setMostrarFiltros(false);
      }
    };

    if (mostrarFiltros) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [mostrarFiltros]);

  // Cerrar menú con Escape
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape' && mostrarFiltros) {
        setMostrarFiltros(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [mostrarFiltros]);

  // Cerrar cualquier desplegable abierto al hacer clic fuera o pulsar Escape
  useEffect(() => {
    if (!menuAbierto) return;
    const handleClick = (e) => {
      if (!e.target.closest('.desplegable-wrap')) setMenuAbierto(null);
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') setMenuAbierto(null);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [menuAbierto]);

  // Loader mínimo de 1 segundo
  useEffect(() => {
    const timer = setTimeout(() => {
      setMinimoTiempoCumplido(true);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  // Guardar filtros en localStorage cuando cambien
  useEffect(() => {
    const filtros = {
      selectedEstados, selectedDepts, selectedChats, selectedPrioridades, selectedTecnicos,
      fechaDesde, fechaHasta, soloMios, soloSinActividad, soloAlerta, searchText, creador
    };
    localStorage.setItem('ticketManagerFiltros', JSON.stringify(filtros));
  }, [selectedEstados, selectedDepts, selectedChats, selectedPrioridades, selectedTecnicos, fechaDesde, fechaHasta, soloMios, soloSinActividad, soloAlerta, searchText, creador]);

  // Abiertos, archivados y técnicos: tres cachés independientes que comparte
  // con el dashboard de staff, así entrar aquí desde el panel no repite las
  // mismas peticiones.
  const [consultaAbiertos, consultaArchivados, consultaTecnicos] = useQueries({
    queries: [
      {
        queryKey: claves.ticketsTodos(),
        queryFn: async () => (await axios.get(`${BASE_URL}/tickets/total/?page=1&limit=1000`, cabecerasAuth())).data.tickets || [],
      },
      {
        queryKey: claves.ticketsArchivados(),
        queryFn: async () => (await axios.get(`${BASE_URL}/tickets/total-archivados/?page=1&limit=1000`, cabecerasAuth())).data.tickets || [],
      },
      {
        queryKey: claves.tecnicos(),
        queryFn: async () => (await axios.get(`${BASE_URL}/usuario/tecnicos`, cabecerasAuth())).data.usuarios || [],
        // La plantilla técnica cambia muy de vez en cuando
        staleTime: 5 * 60_000,
      },
    ],
  });

  const todosLosTickets = useMemo(() => {
    const todos = [...(consultaAbiertos.data || []), ...(consultaArchivados.data || [])];
    return todos.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [consultaAbiertos.data, consultaArchivados.data]);

  const tecnicos = consultaTecnicos.data || [];
  const datosCargados = !consultaAbiertos.isPending && !consultaArchivados.isPending;

  /** Aplica el cambio en las dos cachés de tickets (abiertos y archivados). */
  const aplicarEnCache = (identificadores, campo, valor) => {
    const ids = new Set(identificadores);
    [claves.ticketsTodos(), claves.ticketsArchivados()].forEach((clave) => {
      queryClient.setQueryData(clave, (lista = []) => (
        lista.map((ticket) => (ids.has(ticket.identificador) ? { ...ticket, [campo]: valor } : ticket))
      ));
    });
  };

  // Conteos por categoría (sobre el total, no sobre lo ya filtrado)
  const contarPorValor = (campo) => {
    const counts = {};
    todosLosTickets.forEach(t => {
      const v = t[campo];
      counts[v] = (counts[v] || 0) + 1;
    });
    return counts;
  };
  const conteoEstados = contarPorValor('estado_del_ticket');
  const conteoDepts = contarPorValor('dpto');
  const conteoChats = contarPorValor('estado_del_chat');
  const conteoPrioridades = contarPorValor('prioridad');
  const conteoTecnicos = contarPorValor('usuario_encargado');

  // Filtrado
  const ticketsFiltrados = useMemo(() => {
    const ahora = Date.now();
    const q = searchText.trim().toLowerCase();

    return todosLosTickets.filter(ticket => {
      const matchesEstado = selectedEstados.length === 0 || selectedEstados.includes(ticket.estado_del_ticket);

      const matchesSearch = !q ||
        ticket.asunto.toLowerCase().includes(q) ||
        ticket.identificador.toString().toLowerCase().includes(q) ||
        `${ticket.nombre} ${ticket.apellidos}`.toLowerCase().includes(q) ||
        (ticket.email || '').toLowerCase().includes(q) ||
        (ticket.telefono || '').toLowerCase().includes(q);

      // Si el admin/técnico tiene dpto_tecnico definido, SOLO puede ver tickets de su departamento
      const matchesDept = tokenInfo.dpto_tecnico
        ? ticket.dpto.toLowerCase() === tokenInfo.dpto_tecnico.toLowerCase()
        : (selectedDepts.length === 0 || selectedDepts.includes(ticket.dpto));

      const matchesChat = selectedChats.length === 0 || selectedChats.includes(ticket.estado_del_chat);
      const matchesPrio = selectedPrioridades.length === 0 || selectedPrioridades.includes(ticket.prioridad);
      const matchesTecnico = selectedTecnicos.length === 0 || selectedTecnicos.includes(ticket.usuario_encargado);
      const matchesMios = !soloMios || ticket.usuario_encargado === tokenInfo.usuario;
      const matchesCreador = !creador || ticket.usuario_creador === creador;

      const fechaTicket = new Date(ticket.createdAt);
      const matchesDesde = !fechaDesde || fechaTicket >= new Date(`${fechaDesde}T00:00:00`);
      const matchesHasta = !fechaHasta || fechaTicket <= new Date(`${fechaHasta}T23:59:59`);

      const horasInactivo = (ahora - new Date(ticket.updatedAt || ticket.createdAt).getTime()) / 3600000;
      const matchesSinActividad = !soloSinActividad || (ticket.estado_del_ticket !== 'cerrado' && horasInactivo >= UMBRAL_SIN_ACTIVIDAD_HORAS);
      const matchesAlerta = !soloAlerta || (ticket.estado_del_ticket !== 'cerrado' && ticket.estado_del_chat === 'sin_responder' && horasInactivo >= UMBRAL_ALERTA_HORAS);

      return matchesEstado && matchesSearch && matchesDept && matchesChat && matchesPrio &&
        matchesTecnico && matchesMios && matchesCreador && matchesDesde && matchesHasta &&
        matchesSinActividad && matchesAlerta;
    });
  }, [
    todosLosTickets, selectedEstados, searchText, creador, selectedDepts, selectedChats, selectedPrioridades,
    selectedTecnicos, soloMios, soloSinActividad, soloAlerta, fechaDesde, fechaHasta,
    tokenInfo.dpto_tecnico, tokenInfo.usuario
  ]);

  // Orden
  const ticketsOrdenados = useMemo(() => {
    const arr = [...ticketsFiltrados];
    arr.sort((a, b) => {
      const va = obtenerValorOrden(a, sortField);
      const vb = obtenerValorOrden(b, sortField);
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [ticketsFiltrados, sortField, sortDir]);

  // Resetear a la primera página cuando cambian filtros, orden o tamaño de página
  useEffect(() => {
    setCurrentPage(1);
  }, [ticketsFiltrados, sortField, sortDir, ticketsPerPage]);

  // Paginación
  const totalPages = Math.max(1, Math.ceil(ticketsOrdenados.length / ticketsPerPage));
  const startIdx = (currentPage - 1) * ticketsPerPage;
  const currentTickets = ticketsOrdenados.slice(startIdx, startIdx + ticketsPerPage);

  const handlePageChange = newPage => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const copiarIdentificador = async (event, identificador) => {
    event.stopPropagation();
    try {
      await navigator.clipboard.writeText(identificador);
      toast.success(t('gestorTickets.identificadorCopiado', { identificador }));
    } catch (copyError) {
      console.error('No se pudo copiar el identificador del ticket:', copyError);
      toast.error(t('gestorTickets.errorCopiarIdentificador'));
    }
  };

  const cambiarOrden = (campo) => {
    if (sortField === campo) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(campo);
      setSortDir('asc');
    }
  };

  const indicadorOrden = (campo) => {
    if (sortField !== campo) return null;
    return sortDir === 'asc' ? <FiChevronUp className="th-sort-flecha" /> : <FiChevronDown className="th-sort-flecha" />;
  };

  // Limpiar todos los filtros
  const limpiarFiltros = () => {
    setSelectedEstados([]);
    setSelectedDepts([]);
    setSelectedChats([]);
    setSelectedPrioridades([]);
    setSelectedTecnicos([]);
    setFechaDesde('');
    setFechaHasta('');
    setSoloMios(false);
    setSoloSinActividad(false);
    setSoloAlerta(false);
    setSearchText('');
    setCreador('');
    localStorage.removeItem('ticketManagerFiltros');
  };

  // Eliminar un filtro activo específico
  const eliminarFiltro = (filtro) => {
    switch (filtro.tipo) {
      case 'estado': setSelectedEstados(prev => prev.filter(v => v !== filtro.valor)); break;
      case 'dept': setSelectedDepts(prev => prev.filter(v => v !== filtro.valor)); break;
      case 'chat': setSelectedChats(prev => prev.filter(v => v !== filtro.valor)); break;
      case 'prioridad': setSelectedPrioridades(prev => prev.filter(v => v !== filtro.valor)); break;
      case 'tecnico': setSelectedTecnicos(prev => prev.filter(v => v !== filtro.valor)); break;
      case 'fechaDesde': setFechaDesde(''); break;
      case 'fechaHasta': setFechaHasta(''); break;
      case 'soloMios': setSoloMios(false); break;
      case 'soloSinActividad': setSoloSinActividad(false); break;
      case 'soloAlerta': setSoloAlerta(false); break;
      case 'busqueda': setSearchText(''); break;
      case 'creador': setCreador(''); break;
      default: break;
    }
  };

  const obtenerFiltrosActivos = () => {
    const filtros = [];
    selectedEstados.forEach(v => filtros.push({ tipo: 'estado', valor: v, label: t('gestorTickets.estado'), texto: t(`gestorTickets.estados.${v}`, { defaultValue: v }) }));
    if (!tokenInfo.dpto_tecnico) {
      selectedDepts.forEach(v => filtros.push({ tipo: 'dept', valor: v, label: t('gestorTickets.departamentoCorto'), texto: t(`gestorTickets.departamentos.${v}`, { defaultValue: v }) }));
    }
    selectedChats.forEach(v => filtros.push({ tipo: 'chat', valor: v, label: t('gestorTickets.chat'), texto: t(`gestorTickets.estadosChat.${v}`, { defaultValue: v }) }));
    selectedPrioridades.forEach(v => filtros.push({ tipo: 'prioridad', valor: v, label: t('gestorTickets.prioridad'), texto: t(`gestorTickets.prioridades.${v}`, { defaultValue: v }) }));
    selectedTecnicos.forEach(v => filtros.push({ tipo: 'tecnico', valor: v, label: t('gestorTickets.tecnico'), texto: v === 'sin_asignar' ? t('gestorTickets.sinAsignar') : `@${v}` }));
    if (fechaDesde) filtros.push({ tipo: 'fechaDesde', label: t('gestorTickets.desde'), texto: fechaDesde });
    if (fechaHasta) filtros.push({ tipo: 'fechaHasta', label: t('gestorTickets.hasta'), texto: fechaHasta });
    if (soloMios) filtros.push({ tipo: 'soloMios', label: t('gestorTickets.tecnico'), texto: t('gestorTickets.soloMisTickets') });
    if (soloSinActividad) filtros.push({ tipo: 'soloSinActividad', label: t('gestorTickets.actividad'), texto: t('gestorTickets.sinActividadDias', { dias: UMBRAL_SIN_ACTIVIDAD_HORAS / 24 }) });
    if (soloAlerta) filtros.push({ tipo: 'soloAlerta', label: t('gestorTickets.alerta'), texto: t('gestorTickets.esperandoRespuestaHoras', { horas: UMBRAL_ALERTA_HORAS }) });
    if (creador) filtros.push({ tipo: 'creador', label: t('gestorTickets.creador'), texto: `@${creador}` });
    if (searchText) filtros.push({ tipo: 'busqueda', label: t('gestorTickets.busqueda'), texto: searchText });
    return filtros;
  };

  const filtrosActivos = obtenerFiltrosActivos();

  // Vistas guardadas: capturar/aplicar el set de filtros completo
  const capturarFiltrosActuales = () => ({
    selectedEstados, selectedDepts, selectedChats, selectedPrioridades, selectedTecnicos,
    fechaDesde, fechaHasta, soloMios, soloSinActividad, soloAlerta, searchText, creador
  });

  const guardarVistaActual = () => {
    const nombre = nombreNuevaVista.trim();
    if (!nombre) {
      toast.error(t('gestorTickets.nombreVistaRequerido'));
      return;
    }
    const nuevaVista = { nombre, filtros: capturarFiltrosActuales() };
    const nuevas = [...vistasGuardadas.filter(v => v.nombre !== nombre), nuevaVista];
    setVistasGuardadas(nuevas);
    localStorage.setItem('ticketManagerVistas', JSON.stringify(nuevas));
    setNombreNuevaVista('');
    toast.success(t('gestorTickets.vistaGuardada', { nombre }));
  };

  const aplicarVista = (vista) => {
    const f = vista.filtros;
    setSelectedEstados(f.selectedEstados || []);
    setSelectedDepts(f.selectedDepts || []);
    setSelectedChats(f.selectedChats || []);
    setSelectedPrioridades(f.selectedPrioridades || []);
    setSelectedTecnicos(f.selectedTecnicos || []);
    setFechaDesde(f.fechaDesde || '');
    setFechaHasta(f.fechaHasta || '');
    setSoloMios(!!f.soloMios);
    setSoloSinActividad(!!f.soloSinActividad);
    setSoloAlerta(!!f.soloAlerta);
    setSearchText(f.searchText || '');
    setCreador(f.creador || '');
  };

  const eliminarVista = (nombre) => {
    const nuevas = vistasGuardadas.filter(v => v.nombre !== nombre);
    setVistasGuardadas(nuevas);
    localStorage.setItem('ticketManagerVistas', JSON.stringify(nuevas));
  };

  // Selección múltiple
  const toggleSeleccionado = (identificador) => {
    setSeleccionados(prev => {
      const next = new Set(prev);
      if (next.has(identificador)) next.delete(identificador);
      else next.add(identificador);
      return next;
    });
  };

  const todosPaginaSeleccionados = currentTickets.length > 0 && currentTickets.every(t => seleccionados.has(t.identificador));

  const toggleSeleccionarTodos = () => {
    setSeleccionados(prev => {
      const next = new Set(prev);
      if (todosPaginaSeleccionados) {
        currentTickets.forEach(t => next.delete(t.identificador));
      } else {
        currentTickets.forEach(t => next.add(t.identificador));
      }
      return next;
    });
  };

  // Actualización inline (una fila)
  const actualizarTicketInline = async (identificador, campo, valor, endpoint) => {
    const body = { identificador, [campo]: valor };
    try {
      await axios.put(`${BASE_URL}${endpoint}`, body, cabecerasAuth());
      aplicarEnCache([identificador], campo, valor);
      queryClient.invalidateQueries({ queryKey: claves.estadisticasTickets() });
    } catch (err) {
      console.error('Error al actualizar ticket:', err);
      toast.error(t('gestorTickets.errorActualizarTicket'));
    }
  };

  // Acciones masivas
  const aplicarCambioMasivo = async (accion, valor) => {
    if (seleccionados.size === 0 || !valor) return;
    setProcesandoBulk(true);
    const ids = Array.from(seleccionados);
    const endpoints = {
      estado: { endpoint: '/actualizar-estado_del_ticket', campo: 'estado_del_ticket' },
      prioridad: { endpoint: '/actualizar-prioridad', campo: 'prioridad' },
      tecnico: { endpoint: '/actualizar-tecnico', campo: 'usuario_encargado' },
    };
    const { endpoint, campo } = endpoints[accion];

    try {
      await Promise.all(ids.map(id => axios.put(`${BASE_URL}${endpoint}`, { identificador: id, [campo]: valor }, cabecerasAuth())));
      aplicarEnCache(ids, campo, valor);
      queryClient.invalidateQueries({ queryKey: claves.estadisticasTickets() });
      toast.success(t('gestorTickets.ticketsActualizados', { count: ids.length }));
      setSeleccionados(new Set());
    } catch (err) {
      console.error('Error en acción masiva:', err);
      toast.error(t('gestorTickets.errorActualizarAlgunosTickets'));
    } finally {
      setProcesandoBulk(false);
    }
  };

  const esMenuAbierto = (scope, id, campo) =>
    !!menuAbierto && menuAbierto.scope === scope && menuAbierto.id === id && menuAbierto.campo === campo;

  const abrirMenu = (scope, id, campo) => {
    setMenuAbierto(prev => (prev && prev.scope === scope && prev.id === id && prev.campo === campo) ? null : { scope, id, campo });
  };

  // Desplegable genérico: botón que muestra el valor actual + lista de opciones marcables (sin <select> nativo)
  const renderDesplegable = ({ scope, id, campo, valorActual, opciones, claseTrigger, onSelect, triggerContent, disabled }) => {
    const abierto = esMenuAbierto(scope, id, campo);
    return (
      <div className="desplegable-wrap" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className={`desplegable-trigger ${claseTrigger || 'neutro'}`}
          onClick={() => !disabled && abrirMenu(scope, id, campo)}
          disabled={disabled}
        >
          {triggerContent}
          <FiChevronDown className="desplegable-flecha" />
        </button>
        {abierto && (
          <div className="desplegable-menu">
            {opciones.map(opt => (
              <button
                key={opt.value}
                type="button"
                className={`desplegable-item ${opt.value === valorActual ? 'activo' : ''}`}
                onClick={() => { onSelect(opt.value); setMenuAbierto(null); }}
              >
                <span className="desplegable-check">{opt.value === valorActual && <FiCheck />}</span>
                <span>{opt.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  if (!datosCargados || !minimoTiempoCumplido) {
    return (
      <div className="loader-fullscreen">
        <Loader />
      </div>
    );
  }

  if (tokenInfo.estado === "verificar") {
    return <Verificacion />;
  }

  return (
    <main className='site-width'>
      <ToastContainer position="bottom-right" autoClose={3000} />
      <div className="dashboard-content gap-tickets">
        <div className="gn-header">
          <div className="pagina-cabecera">
            <h2>{t('gestorTickets.titulo')}</h2>
            <p>{t('gestorTickets.subtitulo')}</p>
          </div>
          <div className="gn-header-acciones">
            <button
              type="button"
              className="boton-primario gn-boton-nueva"
              onClick={() => navigate('/nuevo-ticket')}
            >
              <FiPlus /> {t('menu.nuevoTicket')}
            </button>
          </div>
        </div>

        <div className="layout-tickets">
          {/* Menú lateral de filtros */}
          <div className={`sidebar-filtros-overlay ${mostrarFiltros ? 'open' : ''}`} onClick={() => setMostrarFiltros(false)} />
          <aside
            ref={menuRef}
            className={`sidebar-filtros${mostrarFiltros ? ' open' : ''}`}
          >
            <FiltroBusqueda
              title={t('gestorTickets.filtrarTickets')}
              value={searchText}
              onChange={setSearchText}
              placeholder={t('gestorTickets.buscarPlaceholder')}
              className="sidebar-filtros-content"
              showInput={false}
              onClose={() => setMostrarFiltros(false)}
              showFooter={true}
              onReset={limpiarFiltros}
              resetText={t('gestorTickets.resetearFiltros')}
            >
              <div className="grupo-filtro grupo-filtro-checkbox">
                <label>{t('gestorTickets.estadoTicket')}</label>
                {estadosTicket.map(opt => (
                  <label key={opt.value} className="checkbox-filtro">
                    <input
                      type="checkbox"
                      checked={selectedEstados.includes(opt.value)}
                      onChange={() => toggleArrayValue(selectedEstados, opt.value, setSelectedEstados)}
                    />
                    <span>{opt.label}</span>
                    <span className="checkbox-filtro-count">{conteoEstados[opt.value] || 0}</span>
                  </label>
                ))}
              </div>

              <div className="grupo-filtro grupo-filtro-checkbox">
                <label>{t('gestorTickets.departamento')}</label>
                {tokenInfo.dpto_tecnico ? (
                  <p className="filtro-dpto-fijo">{t(`gestorTickets.departamentos.${tokenInfo.dpto_tecnico}`, { defaultValue: tokenInfo.dpto_tecnico })}</p>
                ) : (
                  departamentos.map(opt => (
                    <label key={opt.value} className="checkbox-filtro">
                      <input
                        type="checkbox"
                        checked={selectedDepts.includes(opt.value)}
                        onChange={() => toggleArrayValue(selectedDepts, opt.value, setSelectedDepts)}
                      />
                      <span>{opt.label}</span>
                      <span className="checkbox-filtro-count">{conteoDepts[opt.value] || 0}</span>
                    </label>
                  ))
                )}
              </div>

              <div className="grupo-filtro grupo-filtro-checkbox">
                <label>{t('gestorTickets.estadoChat')}</label>
                {estadosChat.map(opt => (
                  <label key={opt.value} className="checkbox-filtro">
                    <input
                      type="checkbox"
                      checked={selectedChats.includes(opt.value)}
                      onChange={() => toggleArrayValue(selectedChats, opt.value, setSelectedChats)}
                    />
                    <span>{opt.label}</span>
                    <span className="checkbox-filtro-count">{conteoChats[opt.value] || 0}</span>
                  </label>
                ))}
              </div>

              <div className="grupo-filtro grupo-filtro-checkbox">
                <label>{t('gestorTickets.prioridad')}</label>
                {prioridades.map(opt => (
                  <label key={opt.value} className="checkbox-filtro">
                    <input
                      type="checkbox"
                      checked={selectedPrioridades.includes(opt.value)}
                      onChange={() => toggleArrayValue(selectedPrioridades, opt.value, setSelectedPrioridades)}
                    />
                    <span>{opt.label}</span>
                    <span className="checkbox-filtro-count">{conteoPrioridades[opt.value] || 0}</span>
                  </label>
                ))}
              </div>

              <div className="grupo-filtro grupo-filtro-checkbox">
                <label>{t('gestorTickets.tecnicoAsignado')}</label>
                <label className="checkbox-filtro">
                  <input
                    type="checkbox"
                    checked={selectedTecnicos.includes('sin_asignar')}
                    onChange={() => toggleArrayValue(selectedTecnicos, 'sin_asignar', setSelectedTecnicos)}
                  />
                  <span>{t('gestorTickets.sinAsignar')}</span>
                  <span className="checkbox-filtro-count">{conteoTecnicos['sin_asignar'] || 0}</span>
                </label>
                {tecnicos.map(t => (
                  <label key={t.id} className="checkbox-filtro">
                    <input
                      type="checkbox"
                      checked={selectedTecnicos.includes(t.usuario)}
                      onChange={() => toggleArrayValue(selectedTecnicos, t.usuario, setSelectedTecnicos)}
                    />
                    <span>{t.nombre} {t.apellidos}</span>
                    <span className="checkbox-filtro-count">{conteoTecnicos[t.usuario] || 0}</span>
                  </label>
                ))}
              </div>

              <div className="grupo-filtro">
                <label>{t('gestorTickets.rangoFechas')}</label>
                <div className="rango-fechas">
                  <input type="date" value={fechaDesde} onChange={e => setFechaDesde(e.target.value)} />
                  <span>–</span>
                  <input type="date" value={fechaHasta} onChange={e => setFechaHasta(e.target.value)} />
                </div>
              </div>

              <div className="grupo-filtro grupo-vistas">
                <label>{t('gestorTickets.vistasGuardadas')}</label>
                {vistasGuardadas.length === 0 ? (
                  <p className="vistas-vacio">{t('gestorTickets.sinVistasGuardadas')}</p>
                ) : (
                  <div className="lista-vistas">
                    {vistasGuardadas.map(v => (
                      <div key={v.nombre} className="vista-chip">
                        <button type="button" onClick={() => aplicarVista(v)}>{v.nombre}</button>
                        <button type="button" className="vista-eliminar" onClick={() => eliminarVista(v.nombre)} aria-label={t('gestorTickets.eliminarVista', { nombre: v.nombre })}>✕</button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="guardar-vista">
                  <input
                    type="text"
                    placeholder={t('gestorTickets.nombreVistaPlaceholder')}
                    value={nombreNuevaVista}
                    onChange={e => setNombreNuevaVista(e.target.value)}
                  />
                  <button type="button" onClick={guardarVistaActual}>{t('gestorTickets.guardarVistaActual')}</button>
                </div>
              </div>
            </FiltroBusqueda>
          </aside>

          {/* Contenido principal */}
          <div className="tabcontent">
            <div className='grid-historial'>
              {/* Barra de búsqueda */}
              <div className="filtro-tickets-admin">
                <input
                  type="text"
                  placeholder={t('gestorTickets.buscarPlaceholder')}
                  value={searchText}
                  onChange={e => setSearchText(e.target.value)}
                />
                <div className="filtros-acciones">
                  <button
                    type="button"
                    className={`chip-toggle ${soloMios ? 'activo' : ''}`}
                    onClick={() => setSoloMios(v => !v)}
                  >
                    {t('gestorTickets.misTickets')}
                  </button>
                  <button
                    type="button"
                    className={`chip-toggle ${soloAlerta ? 'activo' : ''}`}
                    onClick={() => setSoloAlerta(v => !v)}
                    title={t('gestorTickets.ticketsSinResponderHoras', { horas: UMBRAL_ALERTA_HORAS })}
                  >
                    ⏱ {t('gestorTickets.esperandoRespuesta')}
                  </button>
                  <button
                    type="button"
                    className={`chip-toggle ${soloSinActividad ? 'activo' : ''}`}
                    onClick={() => setSoloSinActividad(v => !v)}
                    title={t('gestorTickets.ticketsSinActividadDias', { dias: UMBRAL_SIN_ACTIVIDAD_HORAS / 24 })}
                  >
                    {t('gestorTickets.sinActividad')}
                  </button>
                  <select
                    className="selector-page-size"
                    value={ticketsPerPage}
                    onChange={e => setTicketsPerPage(Number(e.target.value))}
                    title={t('gestorTickets.ticketsPorPagina')}
                  >
                    <option value={20}>{t('gestorTickets.porPagina', { n: 20 })}</option>
                    <option value={50}>{t('gestorTickets.porPagina', { n: 50 })}</option>
                    <option value={100}>{t('gestorTickets.porPagina', { n: 100 })}</option>
                  </select>
                  <button
                    className="boton-toggle-filtros"
                    onClick={() => setMostrarFiltros(!mostrarFiltros)}
                  >
                    {t('gestorTickets.filtros')}
                  </button>
                </div>
              </div>

              {/* Filtros activos */}
              {filtrosActivos.length > 0 && (
                <div className="filtros-activos-container">
                  {filtrosActivos.map((filtro, index) => (
                    <div key={index} className="filtro-activo">
                      <span className="filtro-label">{filtro.label}:</span>
                      <span className="filtro-valor">{filtro.texto}</span>
                      <button
                        className="filtro-eliminar"
                        onClick={() => eliminarFiltro(filtro)}
                        title={t('gestorTickets.eliminarFiltro')}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Barra de acciones masivas */}
              {seleccionados.size > 0 && (
                <div className="bulk-toolbar">
                  <span className="bulk-toolbar-count">{t('gestorTickets.seleccionados', { count: seleccionados.size })}</span>
                  {renderDesplegable({
                    scope: 'bulk', id: 'estado', campo: 'valor',
                    valorActual: null,
                    opciones: estadosTicket,
                    disabled: procesandoBulk,
                    onSelect: (v) => aplicarCambioMasivo('estado', v),
                    triggerContent: t('gestorTickets.cambiarEstado'),
                  })}
                  {renderDesplegable({
                    scope: 'bulk', id: 'prioridad', campo: 'valor',
                    valorActual: null,
                    opciones: prioridades,
                    disabled: procesandoBulk,
                    onSelect: (v) => aplicarCambioMasivo('prioridad', v),
                    triggerContent: t('gestorTickets.cambiarPrioridad'),
                  })}
                  {renderDesplegable({
                    scope: 'bulk', id: 'tecnico', campo: 'valor',
                    valorActual: null,
                    opciones: [{ value: 'sin_asignar', label: t('gestorTickets.sinAsignar') }, ...tecnicos.map(tecnico => ({ value: tecnico.usuario, label: `${tecnico.nombre} ${tecnico.apellidos}` }))],
                    disabled: procesandoBulk,
                    onSelect: (v) => aplicarCambioMasivo('tecnico', v),
                    triggerContent: t('gestorTickets.asignarTecnico'),
                  })}
                  <button type="button" className="bulk-cancelar" onClick={() => setSeleccionados(new Set())} disabled={procesandoBulk}>
                    {t('gestorTickets.cancelarSeleccion')}
                  </button>
                </div>
              )}

              {/* Tabla de tickets */}
              <div className="overflow-tabla">
                <div className="panel-tickets ticket-table-surface">
                  <div className="ticket-list-header">
                    <span className="ticket-checkbox-header">
                      <input
                        type="checkbox"
                        checked={todosPaginaSeleccionados}
                        onChange={toggleSeleccionarTodos}
                        aria-label={t('gestorTickets.seleccionarTodosPagina')}
                      />
                    </span>
                    <button type="button" className="th-sort" onClick={() => cambiarOrden('asunto')}>{t('gestorTickets.ticket')}{indicadorOrden('asunto')}</button>
                    <button type="button" className="th-sort" onClick={() => cambiarOrden('estado_del_ticket')}>{t('gestorTickets.estado')}{indicadorOrden('estado_del_ticket')}</button>
                    <button type="button" className="th-sort" onClick={() => cambiarOrden('dpto')}>{t('gestorTickets.departamentoCorto')}{indicadorOrden('dpto')}</button>
                    <p>{t('gestorTickets.chat')}</p>
                    <button type="button" className="th-sort" onClick={() => cambiarOrden('prioridad')}>{t('gestorTickets.prioridad')}{indicadorOrden('prioridad')}</button>
                    <button type="button" className="th-sort" onClick={() => cambiarOrden('createdAt')}>{t('gestorTickets.fecha')}{indicadorOrden('createdAt')}</button>
                    <button type="button" className="th-sort" onClick={() => cambiarOrden('usuario_encargado')}>{t('gestorTickets.tecnico')}{indicadorOrden('usuario_encargado')}</button>
                  </div>

                  <div className="tickets-list">
                    {currentTickets.length === 0 ? (
                      <div className="no-tickets">{t('gestorTickets.sinTicketsEncontrados')}</div>
                    ) : (
                      currentTickets.map((ticket) => {
                        const mensajesPendientes = ticket.estado_del_chat === 'sin_responder'
                          ? contarMensajesPendientes(ticket.chat)
                          : 0;
                        const horasInactivo = (Date.now() - new Date(ticket.updatedAt || ticket.createdAt).getTime()) / 3600000;
                        const enAlerta = ticket.estado_del_ticket !== 'cerrado' && ticket.estado_del_chat === 'sin_responder' && horasInactivo >= UMBRAL_ALERTA_HORAS;
                        const esUrgenteSinAsignar = ticket.prioridad === 'urgente' && ticket.usuario_encargado === 'sin_asignar' && ticket.estado_del_ticket !== 'cerrado';

                        return (
                          <div
                            key={ticket.identificador}
                            className='link-ticket'
                            role="link"
                            tabIndex={0}
                            onClick={() => navigate(`/ticket/${ticket.identificador}`)}
                            onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/ticket/${ticket.identificador}`); }}
                          >
                            <div className={`ticket-item ${esUrgenteSinAsignar ? 'ticket-item-alerta' : ''}`}>
                              <div className="ticket-field ticket-checkbox" onClick={(e) => e.stopPropagation()}>
                                <input
                                  type="checkbox"
                                  checked={seleccionados.has(ticket.identificador)}
                                  onChange={() => toggleSeleccionado(ticket.identificador)}
                                  aria-label={t('gestorTickets.seleccionarTicket', { identificador: ticket.identificador })}
                                />
                              </div>

                              <div className="ticket-field ticket-info">
                                <div className="ticket-info-texto">
                                  <span className="ticket-info-id">
                                    #{ticket.identificador}
                                    <button
                                      type="button"
                                      className="ticket-id-copy"
                                      onClick={(event) => copiarIdentificador(event, ticket.identificador)}
                                      aria-label={t('gestorTickets.copiarIdentificadorAria', { identificador: ticket.identificador })}
                                      title={t('gestorTickets.copiarIdentificador')}
                                    >
                                      <FiCopy />
                                    </button>
                                  </span>
                                  <span className="ticket-info-asunto">
                                    {ticket.asunto.split(' ').slice(0, 5).join(' ')}
                                    {ticket.asunto.split(' ').length > 5 ? '...' : ''}
                                  </span>
                                  <span className="ticket-info-creador">{ticket.nombre} {ticket.apellidos}</span>
                                  {esUrgenteSinAsignar && <span className="badge-urgente-sin-asignar">⚠ {t('gestorTickets.urgenteSinAsignar')}</span>}
                                </div>
                              </div>

                              <div className="ticket-field estado">
                                {renderDesplegable({
                                  scope: 'fila', id: ticket.identificador, campo: 'estado_del_ticket',
                                  valorActual: ticket.estado_del_ticket,
                                  opciones: estadosTicket,
                                  claseTrigger: ticket.estado_del_ticket,
                                  onSelect: (v) => actualizarTicketInline(ticket.identificador, 'estado_del_ticket', v, '/actualizar-estado_del_ticket'),
                                  triggerContent: t(`gestorTickets.estados.${ticket.estado_del_ticket}`, { defaultValue: ticket.estado_del_ticket }),
                                })}
                              </div>

                              <div className="ticket-field dpto">
                                {renderDesplegable({
                                  scope: 'fila', id: ticket.identificador, campo: 'dpto',
                                  valorActual: ticket.dpto,
                                  opciones: departamentos,
                                  claseTrigger: claseDpto(ticket.dpto),
                                  onSelect: (v) => actualizarTicketInline(ticket.identificador, 'dpto', v, '/actualizar-dpto'),
                                  triggerContent: t(`gestorTickets.departamentos.${ticket.dpto}`, { defaultValue: ticket.dpto }),
                                })}
                              </div>

                              <div className="ticket-field chat-estado">
                                {ticket.estado_del_chat === 'sin_responder' && (
                                  <p className='sin-responder'>
                                    {t('gestorTickets.estadosChat.sin_responder')}
                                    {mensajesPendientes > 0 && <span className="chat-badge-count">{mensajesPendientes}</span>}
                                  </p>
                                )}
                                {ticket.estado_del_chat === 'respondido' && <p className='respondido'>{t('gestorTickets.estadosChat.respondido')}</p>}
                                {enAlerta && (
                                  <span className="chat-alerta" title={t('gestorTickets.esperandoRespuestaDesdeHoras', { horas: UMBRAL_ALERTA_HORAS })}>
                                    ⏱ {formatearFechaRelativa(ticket.updatedAt || ticket.createdAt, t, i18n.language)}
                                  </span>
                                )}
                              </div>

                              <div className="ticket-field prioridad">
                                {renderDesplegable({
                                  scope: 'fila', id: ticket.identificador, campo: 'prioridad',
                                  valorActual: ticket.prioridad || 'no_urgente',
                                  opciones: prioridades,
                                  claseTrigger: ticket.prioridad === 'urgente' ? 'alta' : 'sin-prioridad',
                                  onSelect: (v) => actualizarTicketInline(ticket.identificador, 'prioridad', v, '/actualizar-prioridad'),
                                  triggerContent: t(`gestorTickets.prioridades.${ticket.prioridad || 'no_urgente'}`),
                                })}
                              </div>

                              <div className="ticket-field fecha" title={new Date(ticket.createdAt).toLocaleString(i18n.language)}>
                                {formatearFechaRelativa(ticket.createdAt, t, i18n.language)}
                              </div>

                              <div className="ticket-field asignado">
                                {ticket.usuario_encargado === 'sin_asignar' ? (
                                  <span className="dt-asignado-icono dt-asignado-icono-vacio"><FiUserX /></span>
                                ) : (
                                  <span className="dt-asignado-icono dt-asignado-icono-activo"><FiUserCheck /></span>
                                )}
                                <span>{ticket.usuario_encargado === 'sin_asignar' ? t('gestorTickets.sinAsignar') : `@${ticket.usuario_encargado}`}</span>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>

              {/* Paginación */}
              {ticketsOrdenados.length > 0 && totalPages > 1 && (
                <div className="pagination">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    {t('gestorTickets.anterior')}
                  </button>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    {t('gestorTickets.siguiente')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default TicketManager;
